<script setup lang="ts">
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import { h, ref } from 'vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { useMemoryChallengeLeaderboard } from '@/composables/game/leader-board'
import { useMemoryChallenge } from '@/composables/game/challenges'
import { usePlayer } from '@/composables/game/player'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import { MINI_GAME_ID } from '@/types/shared/player-config'
import memoryChallengeMainImage from '@/assets/images/challenges/memory-challenge-main.webp'
import api from '@/services/api'
import { useToaster } from '@/composables/game/toasters'
import { useRouter } from 'vue-router'
import { RouteName } from '@/routes'
import { useOutOfSpecialCurrency, usePathwayEventOverlay, usePathwayEvents } from '@/composables/game/pathway-events'
import { useMemoryChallengeModal } from '@/composables/game/memory-challenge-modal'
import MemoryChallengeRulesModal from '@/components/modals/MemoryChallengeRulesModal.vue'
import OutOfSpecialCurrencyModal from '@/components/modals/OutOfSpecialCurrencyModal.vue'
import BaseChallengeOverlay from '@/components/overlays/BaseChallengeOverlay.vue'
import ClockSvgIcon from '@/assets/images/clock-icon.svg'

defineProps<{
	show: boolean
}>()
const emit = defineEmits<{
	hide: []
	play: [nonce: string]
}>()

const { notify } = useToaster()
const router = useRouter()
const { pathwayEvents, anyActivePathwayEvent } = usePathwayEvents()
const { showFirstPathway } = usePathwayEventOverlay()
const { isReturningFromGame } = useMemoryChallengeModal()
const {
  selectedSpecialCurrencyForModal,
  canShowOutOfSpecialCurrencyModal,
  getAvailableEcommerceItems,
  closeOutOfSpecialCurrencyModal,
} = useOutOfSpecialCurrency()

const { player, updatePlayer } = usePlayer()
const { addHapticFeedback } = useHapticFeedback()
const { open } = useTonConnectModal()
const { rawAddress } = useWalletConnect()
const {
  getMemoryChallengeLeaderboard,
  memoryChallengeLeaderboard: players,
  currentPlayer,
} = useMemoryChallengeLeaderboard()
const {
  formattedCountdown,
  memoryChallenge,
  entryFee,
  entryCurrency,
  currencyImage,
} = useMemoryChallenge()

const buyingEntry = ref(false)
const showRulesModal = ref(false)

async function onCtaClick() {
  addHapticFeedback('medium')
  if (!rawAddress.value) {
    open()
    return
  }

  const playerCurrency = player.value.specialCurrencies[entryCurrency.value] || 0
  if (playerCurrency < entryFee.value) {
    if (canShowOutOfSpecialCurrencyModal(entryCurrency.value)) {
      selectedSpecialCurrencyForModal.value = entryCurrency.value
      return
    }
    notify({
      type: 'error',
      title: `<span class="flex items-center gap-1.5">Not enough <img width="14px" height="14px" style="display: inline-block; vertical-align: middle;" src="${currencyImage.value}" alt="Icon"></span>`,
      description: h('span', { class: 'font-medium' }, [
        h('span', {
          class: 'underline text-teal-primary cursor-pointer',
          onClick: () => {
            if (anyActivePathwayEvent.value) {
              showFirstPathway(pathwayEvents.value)
            } else {
              router.push({ name: RouteName.HOME })
            }
            emit('hide')
          },
        }, 'Explore pathways'),
        ' to get more now!',
      ]),
    })
    return
  }

  buyingEntry.value = true
  try {
    const { playerStats, nonce, error } = await api.buyMiniGameEntry({ gameId: MINI_GAME_ID.MEMORY_GAME })

    if (error?.includes('another_entry_in_progress')) {
      notify({
        type: 'error',
        title: 'Purchase Failed',
        description: 'Please wait 1 minute for the next play',
      })
      return
    }

    if (!nonce) {
      throw new Error('No nonce received from server')
    }
    updatePlayer(playerStats)
    const url = `/${MINI_GAME_ID.MEMORY_GAME}?nonce=${nonce}`
    window.history.replaceState({}, '', url)
    window.location.reload()
  } catch (error) {
    console.error('Failed to buy memory game entry:', error)
    notify({
      type: 'error',
      title: 'Purchase Failed',
      description: 'Failed to buy memory game entry',
    })
  } finally {
    setTimeout(() => buyingEntry.value = false, 500)
  }
}
</script>

<template>
  <BaseChallengeOverlay
    :show="show"
    :challenge="memoryChallenge"
    :formatted-countdown="formattedCountdown"
    :on-cta-click="onCtaClick"
    :get-leaderboard="getMemoryChallengeLeaderboard"
    :players="players"
    :current-player="currentPlayer"
    :main-image="memoryChallengeMainImage"
    main-image-background-class="bg-[linear-gradient(135deg,#BBEE53_0%,#F0323E_100%)]"
    timer-background-class="bg-[linear-gradient(90deg,#F32A3B_0%,#A7C568_30.35%,#CE583D_80.77%)]"
    title="Memory Challenge"
    description="Match pairs as fast as you can and climb the leaderboard!"
    cta-text="PLAY"
    :is-rule-card-clickable="true"
    rule-card-line1="Solve Memory"
    rule-card-line2="game"
    :score-image="ClockSvgIcon"
    :currency-image="currencyImage"
    :challenge-currency="entryCurrency"
    :is-returning-from-game="isReturningFromGame"
    :is-cta-button-disabled="buyingEntry"
    :is-score-in-milliseconds="true"
    @hide="emit('hide')"
    @open-rule-modal="showRulesModal = true"
  >
    <template #ctaButton>
      <template v-if="buyingEntry">
        <span class="text-lg font-bold">PROCESSING...</span>
      </template>
      <template v-else>
        <span class="text-lg font-bold tracking-[1px]">PLAY FOR</span>
        <img
          :src="currencyImage"
          class="size-5 mx-1.5"
          :alt="entryCurrency"
        >
        <span class="text-lg font-bold">{{ entryFee }}</span>
      </template>
    </template>
  </BaseChallengeOverlay>

  <MemoryChallengeRulesModal
    :show="showRulesModal"
    @hide="showRulesModal = false"
  />
  <OutOfSpecialCurrencyModal
    v-if="selectedSpecialCurrencyForModal"
    :show="!!selectedSpecialCurrencyForModal"
    :currency="selectedSpecialCurrencyForModal"
    :ecommerce-items="getAvailableEcommerceItems(selectedSpecialCurrencyForModal)"
    subtitle="Get more and keep playing!"
    :has-checkmark="false"
    @hide="closeOutOfSpecialCurrencyModal($event)"
  />
</template>
